"use client"

import clsx from "clsx"
import { FormattedMessage, FormattedNumber, useIntl } from "react-intl"
import { twMerge } from "tailwind-merge"

import {
  appendReferralCode,
  Box,
  Button,
  Experience,
  H4,
  Icon,
  isWeb,
  makeMessengerUri,
  P,
  useExperienceMessageChannel,
  useExperienceUrl,
  User,
  useReferralCode,
} from "@bullseye/components"

import {
  ActivityRemainingSpots,
  AddToCalendarButton,
  ShareButton,
} from "../../../../components"
import { AttendButton } from "./AttendButton"
import { ManageActivityButton } from "./ManageActivityButton"
import { WaitlistCurrentCount, WaitlistInfo } from "./WaitlistInfo"

type Props = {
  experience: Experience
  className?: string
  user?: User
}

const AttendingBanner = ({ experience, user }: Props) => {
  const isOrganizer = experience.organizer_id === user?.id
  const isAttending = experience.requester?.is_attending
  const intl = useIntl()
  const baseUrl = useExperienceUrl(experience.slug)
  const { code: referralCode } = useReferralCode()
  const url = appendReferralCode(baseUrl, referralCode)

  if (!isOrganizer && !isAttending) return null

  const message = intl.formatMessage(
    { id: "activity.share.message.preRsvp" },
    { title: experience.title, url },
  )
  const subject = intl.formatMessage(
    { id: "activity.share.subject" },
    { title: experience.title },
  )

  return (
    <Box className="flex-row items-center justify-between rounded-lg bg-green-50 p-4 g-2">
      <Box className="flex-row items-center g-2">
        <Box className="rounded-full bg-green-600 p-1">
          <Icon color="text-white" type="check" />
        </Box>
        <P>
          <FormattedMessage
            id={
              isOrganizer
                ? "activity.cta.isOrganizer"
                : "activity.cta.attending"
            }
          />
        </P>
      </Box>
      {isAttending && (
        <ShareButton
          message={message}
          url={url}
          emailSubject={subject}
          buttonType="text"
          variant="secondary"
          underlined
          startIconType={undefined}
          labelId="activity.share.inviteFriend"
        />
      )}
    </Box>
  )
}

export const ActivityCTA = ({ experience, className, user }: Props) => {
  const baseUrl = useExperienceUrl(experience.slug)
  const { code: referralCode } = useReferralCode()
  const experienceUrl = appendReferralCode(baseUrl, referralCode)
  const { messageChannel } = useExperienceMessageChannel(experience.id)
  const intl = useIntl()

  const isOrganizer = experience.organizer_id === user?.id
  const isAttending = experience.requester?.is_attending

  const remainingSpots =
    experience.max_attendees > 0
      ? experience.max_attendees - experience.attendees_count
      : null

  const inviteMessage = intl.formatMessage(
    { id: "activity.share.message.preRsvp" },
    { title: experience.title, url: experienceUrl },
  )
  const inviteSubject = intl.formatMessage(
    { id: "activity.share.subject" },
    { title: experience.title },
  )

  return (
    <Box
      className={twMerge(
        "flex flex-col rounded-none bg-white p-6 g-4 md:rounded-lg",
        className,
      )}
    >
      <AttendingBanner experience={experience} user={user} />
      <Box className="flex flex-col g-1 empty:hidden">
        {!!experience.ticket_price && (
          <H4>
            <FormattedNumber
              style="currency"
              currency="USD"
              value={experience.ticket_price ?? 0}
            />
          </H4>
        )}
        {remainingSpots !== null && (
          <ActivityRemainingSpots
            as={experience.ticket_price ? "p" : "h4"}
            spots={remainingSpots}
          />
        )}
        <WaitlistCurrentCount />
        <WaitlistInfo />
      </Box>
      <Box className="flex flex-col g-4">
        <Box className="g-2">
          <Box className="flex flex-col g-2 md:flex-row">
            <Box className="md:flex-[2]">
              <AttendButton />
            </Box>
            {!isAttending && !isOrganizer && (
              <Box className="md:flex-1">
                <ShareButton
                  message={inviteMessage}
                  url={experienceUrl}
                  emailSubject={inviteSubject}
                  buttonType="button"
                  variant="primary"
                  outlined
                  startIconType="plus"
                  labelId="activity.share.invite"
                />
              </Box>
            )}
          </Box>
          {(isOrganizer || user?.admin) && (
            <ManageActivityButton
              href={experienceUrl}
              experience={experience}
            />
          )}
          <Box
            className={clsx({
              hidden: !isAttending && !isOrganizer,
            })}
          >
            <Button
              as="a"
              variant="secondary"
              startIconType="chat"
              href={makeMessengerUri(messageChannel?.stream_channel_id)}
            >
              <FormattedMessage id="general.ctas.openChat" />
            </Button>
          </Box>
          {isWeb() && (
            <Box
              className={clsx({
                hidden: !isAttending && !isOrganizer,
              })}
            >
              <AddToCalendarButton
                variant="secondary"
                experienceSlug={experience.slug}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
}
